import { useId, useState, type FormEvent } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useLocale } from "@/lib/i18n/LocaleContext";
import { locales, LOCALE_LABELS, type Locale } from "@/lib/i18n/config";
import { submitTranslationSuggestion } from "@/lib/translation-suggestions";
import { Languages, Loader2, CheckCircle2, AlertCircle } from "lucide-react";

type Status = "idle" | "submitting" | "success" | "error";

export function SuggestTranslationModal({
  open,
  onClose,
  itemId,
  itemName,
  itemImage,
}: {
  open: boolean;
  onClose: () => void;
  itemId: string;
  itemName: string;
  itemImage: string | null;
}) {
  const { t } = useLocale();
  const fieldIdPrefix = useId();
  const [names, setNames] = useState<Record<Locale, string>>({
    en: itemName,
    "pt-BR": "",
    es: "",
    id: "",
    th: "",
    de: "",
  });
  const [status, setStatus] = useState<Status>("idle");

  function handleOpenChange(next: boolean) {
    if (!next) {
      onClose();
      setStatus("idle");
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setStatus("submitting");
    const result = await submitTranslationSuggestion({ itemId, names });
    setStatus(result.ok ? "success" : "error");
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-sm gap-0 overflow-hidden rounded-[2rem] border-4 border-white bg-white p-0 shadow-2xl">
        <DialogHeader className="flex-row items-center gap-3 px-6 pb-2 pt-6 text-left">
          <div className="flex size-11 shrink-0 items-center justify-center rounded-2xl bg-amber-100">
            <Languages className="size-5 text-amber-700" />
          </div>
          <div className="min-w-0">
            <DialogTitle className="text-lg font-black tracking-tight">{t("itemFallback.modalTitle")}</DialogTitle>
            <p className="truncate text-sm font-medium text-muted-foreground">{itemName}</p>
          </div>
        </DialogHeader>

        {status === "success" ? (
          <div className="flex flex-col items-center gap-3 px-6 py-8 text-center">
            <CheckCircle2 className="size-10 text-emerald-500" />
            <p className="text-sm font-bold text-slate-700">{t("itemFallback.successMessage")}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 px-6 py-4">
            {itemImage ? <img src={itemImage} alt="" className="mx-auto h-24 w-24 object-contain" /> : null}

            {locales.map((locale) => (
              <div key={locale}>
                <label
                  htmlFor={`${fieldIdPrefix}-${locale}`}
                  className="mb-1 block text-[10px] font-black uppercase tracking-widest text-slate-500"
                >
                  {LOCALE_LABELS[locale]}
                </label>
                <input
                  id={`${fieldIdPrefix}-${locale}`}
                  type="text"
                  value={names[locale]}
                  onChange={(e) => setNames((prev) => ({ ...prev, [locale]: e.target.value }))}
                  placeholder={t("itemFallback.fieldPlaceholder")}
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-800 outline-none transition-colors focus:border-amber-400 focus:bg-white"
                />
              </div>
            ))}

            {status === "error" ? (
              <p className="flex items-center gap-1.5 text-xs font-bold text-rose-600">
                <AlertCircle className="size-3.5 shrink-0" />
                {t("itemFallback.errorMessage")}
              </p>
            ) : null}

            <button
              type="submit"
              disabled={status === "submitting"}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-3 text-xs font-black uppercase tracking-widest text-white transition-colors hover:bg-black disabled:opacity-60"
            >
              {status === "submitting" ? <Loader2 className="size-3.5 animate-spin" /> : null}
              {status === "submitting" ? t("itemFallback.submitting") : t("itemFallback.submitButton")}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
